import React, { useEffect, useState } from 'react';
import { QueryErrorResetBoundary, useIsFetching } from '@tanstack/react-query';
import { auth, config } from '../config';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedLoanNumber } from '@store/actions/widgets.actions';
import { SSOProps } from '@types';
import { ACPTiles } from '@components_v2/UI/organisms/ACPTiles';
import { get, isEmpty } from 'lodash';
import { getAccountConnectToken } from '@services/AccountConnectFormService';
import { clearCookie, getCookieValueByKey, setCookieByKey } from '@utils/cookieHelper';
import AccConnectFormSubmit from '@components/AccountConnectFormSubmit/AccConnectFormSubmit';
import { useNavigate } from 'react-router-dom';
import { DashboardLoadingScreen } from '@components_v2/UI/organisms/DashboardLoader';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { ErrorPage } from '@containers/fullAppV2/components/Error';
import { withHeaderFooter } from '@components_v2/HOCs';
import { useWidgetsQuery } from '@hooks/useWidgetsQuery';
import { useHomeConfig } from '@hooks/useHomeConfig';
import { useAccountsQuery } from '@hooks/useAccountsQuery';
import { i18n as marketingi18n } from '@fmc-digital/marketing';
import { i18n as servicingi18n } from '@fmc-digital/servicing';
import { accessTokenObj } from '@utils/auth.config';
import { activityEventUpdate, setAdditionalProperties } from 'fmc-digital-common';
import { useOktaAuth } from '@okta/okta-react';
import { logUserEvent } from '@analytics/amplifyLogging';
import { selectDashboardType, selectGroupByIdentifiers, selectSelectedLoanNumber } from '@store/selectors/widgets.selectors';
import { useAccountConnectMutation } from '@hooks/useAccountConnectMutation';

const MapToType = {
    ServicingLoanNumber: 'servicing',
    OriginationLoanNumber: 'origination',
    ServicingLoanNumberOriginationLoanNumber: 'both'
};

type LoanType = 'both' | 'servicing' | 'origination';

const DashboardError = withHeaderFooter(ErrorPage, { showDropDownOptions: false, showMenuOptions: false, showFooterAds: false });
const HomeContext = () => {
    const [finish, setFinish] = React.useState(false);

    const { oktaAuth } = useOktaAuth();
    const isFetching = useIsFetching({ fetchStatus: 'fetching', queryKey: ['accounts', 'widgets', 'home', 'auto-pay', 'payment-history'] });
    const selectedLoan = useSelector(selectSelectedLoanNumber);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [ssoData, setSsoData] = useState<SSOProps>({
        url: '',
        token: ''
    });

    const accountDetailSSO = async (url: string) => {
        setLoading(true);
        try {
            const { response } = await getAccountConnectToken(`${config.env('ssoUrl')}`, `${config.env('xApiKey')}`);

            if (response) {
                auth.getOkta().tokenManager.clear();
                clearCookie();
                updateSsoData({ url, token: response?.signinToken });
            } else navigate('error');
        } catch (e) {
            navigate('error');
        } finally {
            setLoading(false);
        }
    };

    const updateSsoData = ({ url, token }: { url: string; token: string }) => {
        setSsoData({
            url,
            token
        });
        setFinish(true);
    };

    const sso = useAccountConnectMutation();
    const identifiers = useSelector(selectGroupByIdentifiers);
    const dashboardType = useSelector(selectDashboardType);
    const loanType: LoanType = get(MapToType, [Object.keys(identifiers).reduce((result, key) => result + key, '')], undefined);

    const { status: homeStatus } = useHomeConfig();
    const { data: widgets, status: wStatus, isSuccess, isStale, error: wError } = useWidgetsQuery({ accountDetailSSO });
    const appName = get(widgets, 'redirect.appName', undefined);

    useAccountsQuery({ enabled: wStatus === 'success' && ['both', 'servicing'].some(t => t === loanType) });

    const firstServicing = get(identifiers, ['ServicingLoanNumber', '0'], undefined);
    const firstOrigination = get(identifiers, ['OriginationLoanNumber', '0'], undefined);

    useEffect(() => {
        const token: any = accessTokenObj();
        setAdditionalProperties('amplify', {
            uuid: token?.uuid,
            userID: token?.sub
        });
    }, []);

    useEffect(() => {
        if (wStatus === 'success' && !finish && isSuccess && isFetching === 0) {
            if (appName === 'account_connect') {
                accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`).catch(console.error);
                setFinish(true);
                return;
            }
            if (selectedLoan) {
                if (selectedLoan.type === 'OriginationLoanNumber') {
                    dispatch(setSelectedLoanNumber(firstServicing ?? firstOrigination));
                    if (!firstServicing) {
                        navigate(`/new-loan/${firstOrigination.identifier}`);
                    }
                } else {
                    dispatch(setSelectedLoanNumber(selectedLoan));
                }
                setFinish(true);
            } else {
                if (loanType === 'origination') {
                    dispatch(setSelectedLoanNumber(firstOrigination));
                    navigate(`/new-loan/${firstOrigination.identifier}`);
                    setFinish(true);
                } else if (loanType === 'servicing' || loanType === 'both') {
                    dispatch(setSelectedLoanNumber(firstServicing));
                    setFinish(true);
                }
            }
        }
    }, [wStatus, appName, loanType, finish, isSuccess, isFetching]);

    useEffect(() => {
        setLoading([homeStatus, wStatus].some(truthy => truthy === 'loading'));
    }, [homeStatus, wStatus]);

    useEffect(() => {
        const token: any = accessTokenObj();
        setAdditionalProperties('amplify', {
            uuid: token?.uuid,
            userID: token?.sub
        });

        if (!getCookieValueByKey('ACPLogin')) {
            const token = oktaAuth.getAccessToken();
            if (token !== undefined) {
                setAdditionalProperties('authToken', token);
                const response = activityEventUpdate(
                    [{ activityType: 'ACPLogin', activityResult: 'SUCCESS' }],
                    config.env('activityUpdateUrl')
                );
                if (response) setCookieByKey('ACPLogin', 'true');
            }
        }
    }, []);

    if (loading || isFetching || !finish) return <DashboardLoadingScreen />;
    if (!isEmpty(ssoData.url) && !isEmpty(ssoData.token))
        return <AccConnectFormSubmit method="post" action={ssoData.url} value={ssoData.token} />;
    return <ACPTiles />;
};

export const HomeContextProvider = () => {
    const token: any = accessTokenObj();
    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ErrorBoundary
                    onError={(error, info) => {
                        console.error(error);
                        console.log(info);
                        try {
                            logUserEvent({ action: 'OnErrorBoundary', description: JSON.stringify(error), type: 'ACPRebrand', ...error });
                        } catch (e) {
                            console.log(e);
                        }
                    }}
                    onReset={reset}
                    fallbackRender={({ resetErrorBoundary }: any) => <DashboardError resetErrorBoundary={resetErrorBoundary} />}
                >
                    {/*                         <I18nextProvider i18n={acpi18n}> */}
                    <I18nextProvider i18n={marketingi18n}>
                        <I18nextProvider i18n={servicingi18n}>
                            <input type="hidden" id="UserUUID" name="UserUUID" value={token?.uuid} />
                            <HomeContext />
                        </I18nextProvider>
                    </I18nextProvider>
                    {/*                         </I18nextProvider> */}
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
};
